import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { Notification } from "@interfaces/notifications";
import {
  formatNotificationFallback,
  formatNotificationListTimestamp,
  formatNotificationTooltip,
  getCategoryBarColor,
  getCategoryTone,
  getNotificationDisplay,
  NotificationCategoryTone,
  NotificationDisplay,
} from "@util/notifications-display.util";
import { tr } from "@util/tr";
import { WsChipType } from "@web/util/ws-ui/ws-chip/ws-chip.component";

@Component({
  selector: "web-notification-panel",
  templateUrl: "./notification-panel.component.html",
  styleUrls: ["./notification-panel.component.scss"],
})
export class NotificationPanelComponent implements OnChanges {
  @Input() notifications: Notification[] | null = [];
  @Input() onlyUnread = false;
  @Input() noMore = false;
  @Input() loading = false;
  @Input() unreadCount = 0;

  @Output() filterChange = new EventEmitter<"all" | "unreaded">();
  @Output() dismissAll = new EventEmitter<void>();
  @Output() notificationClick = new EventEmitter<Notification>();
  @Output() scrollDown = new EventEmitter<void>();

  private scrollLoadPending = false;

  readonly newChipType = WsChipType.TIPOLOGIA_WARNING;
  readonly invoiceChipColor = "#b45309";
  readonly invoiceChipBackground = "#fff7ed";
  readonly shippingChipColor = "#003b71";
  readonly shippingChipBackground = "#eff4fb";

  get visibleNotifications(): Notification[] {
    const list = this.notifications || [];
    if (this.onlyUnread) {
      return list.filter((n) => !n.viewed_at);
    }
    return list;
  }

  get unreadCountLabel(): string {
    return tr("{{count}} new").replace("{{count}}", String(this.unreadCount));
  }

  onFilterClick(filter: "all" | "unreaded"): void {
    if ((filter === "unreaded") === this.onlyUnread) {
      return;
    }
    this.filterChange.emit(filter);
  }

  onNotificationClick(notification: Notification): void {
    this.notificationClick.emit(notification);
  }

  onListScroll(event: Event): void {
    if (this.noMore || this.loading || this.scrollLoadPending) {
      return;
    }
    const element = event.target as HTMLElement;
    const remaining = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (remaining > 80) {
      return;
    }
    this.scrollLoadPending = true;
    this.scrollDown.emit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.loading && !this.loading) {
      this.scrollLoadPending = false;
    }
  }

  displayFor(notification: Notification): NotificationDisplay | null {
    return getNotificationDisplay(notification);
  }

  barColor(notification: Notification): string {
    return getCategoryBarColor(this.displayFor(notification)?.category);
  }

  iconTone(notification: Notification): NotificationCategoryTone {
    return getCategoryTone(this.displayFor(notification)?.category);
  }

  isUnread(notification: Notification): boolean {
    return !notification?.viewed_at;
  }

  titleFor(notification: Notification): string {
    const display = this.displayFor(notification);
    if (display?.title) {
      return display.title;
    }
    const raw = notification?.title || "N/D";
    const entityId = display?.entityId || notification?.payload?.display?.entityId;
    if (entityId && /^\d+$/.test(String(entityId)) && /[0-9a-f]{8}-[0-9a-f]{4}/i.test(raw)) {
      return raw.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i, String(entityId));
    }
    return raw;
  }

  bodyFor(notification: Notification): string {
    return this.displayFor(notification)?.body || notification?.message || "N/D";
  }

  showInvoiceChip(notification: Notification): boolean {
    return !!this.displayFor(notification)?.markers?.invoiceRequested;
  }

  showShippingChip(notification: Notification): boolean {
    return !!this.displayFor(notification)?.markers?.shippingRequested;
  }

  hasMarkerRow(notification: Notification): boolean {
    return this.showInvoiceChip(notification) || this.showShippingChip(notification);
  }

  listTimestamp(notification: Notification): string {
    const ts = notification?.created_at || notification?.send_at;
    return formatNotificationListTimestamp(ts) || formatNotificationFallback(ts);
  }

  tooltipTimestamp(notification: Notification): string {
    const ts = notification?.created_at || notification?.send_at;
    return formatNotificationTooltip(ts);
  }
}
